import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FaArchive, FaTrash } from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { API_BASE_URL } from '../config';
import { useTenant } from '../context/TenantContext';
import { usePermissions } from '../context/PermissionsContext';
import PermissionGuard from './PermissionGuard';
import { PERMISSIONS } from '../constants/permissions';
import LoadingSpinner from './LoadingSpinner';
import { formatCarePlanId } from '../utils/displayIds';
import '../Pages/CareRecipients.css';

const C = {
    dark: '#131926',
    darkMid: '#1e2d40',
    aqua: '#55fff4',
    accent: '#2a7f8a',
    grey: '#d1d2dc',
    textMuted: '#6b7280',
    green: '#10b981',
    amber: '#f59e0b',
    red: '#ef4444',
    blue: '#3b82f6',
    border: '#eaf2f4',
    rowAlt: '#f4fafa',
    headerBg: '#c5e4e7',
    headerText: '#356167',
    surface: '#f4fafa',
    rowHover: '#eaf4f5',
};

const TH = {
    padding: '11px 14px',
    color: C.headerText,
    fontWeight: 600,
    fontSize: '0.76rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    background: 'inherit',
    border: 'none',
    textAlign: 'left',
    whiteSpace: 'nowrap',
};

const TD = {
    padding: '11px 14px',
    borderBottom: `1px solid ${C.border}`,
    fontSize: '0.9rem',
    color: '#1e3a5f',
    verticalAlign: 'middle',
};

function CarePlans() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentTenant, sites } = useTenant();
    const { hasPermission } = usePermissions();
    const [carePlans, setCarePlans] = useState([]);
    const [archivedCarePlans, setArchivedCarePlans] = useState([]);
    const [removedCarePlans, setRemovedCarePlans] = useState([]);
    const [activeTab, setActiveTab] = useState('active');
    const [customerName, setCustomerName] = useState('');
    const [activeQuotes, setActiveQuotes] = useState({});
    const [latestActiveQuoteCarePlanId, setLatestActiveQuoteCarePlanId] = useState(null);
    const [activeQuotesLoaded, setActiveQuotesLoaded] = useState(false);

    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [carePlanToArchive, setCarePlanToArchive] = useState(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [carePlanToRemove, setCarePlanToRemove] = useState(null);
    const [showArchiveAllModal, setShowArchiveAllModal] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

    const showToast = (message, variant = 'success') => setToast({ show: true, message, variant });

    useEffect(() => {
        if (!toast.show) return;
        const timer = setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
        return () => clearTimeout(timer);
    }, [toast.show]);

    const normalizeCarePlan = (plan) => ({
        ...plan,
        care_plan_id: plan.care_plan_id || plan.CarePlanID,
        status: plan.status !== undefined ? plan.status : plan.Status,
        date_created: plan.date_created || plan.DateCreated,
        tenant_id: plan.tenant_id || plan.TenantID,
        site_id: plan.site_id || plan.SiteID,
        tenant_name: plan.tenant_name || plan.TenantName,
        site_name: plan.site_name || plan.SiteName,
        tenant_site_id: plan.tenant_site_id || plan.TenantSiteID,
    });

    const fetchCarePlans = async () => {
        const tenantName = currentTenant?.name;
        const params = new URLSearchParams();
        if (tenantName) params.append('tenant_name', tenantName);
        const queryString = params.toString();
        const url = `${API_BASE_URL}/careplans/${id}${queryString ? '?' + queryString : ''}`;

        try {
            const response = await fetchWithAuth(url);
            const data = await response.json();

            let carePlansArray = null;
            if (data.care_plans) carePlansArray = data.care_plans;
            else if (Array.isArray(data)) carePlansArray = data;
            else if (data.carePlans) carePlansArray = data.carePlans;

            if (carePlansArray && Array.isArray(carePlansArray) && carePlansArray.length > 0) {
                const normalizedPlans = carePlansArray.map(normalizeCarePlan);

                const tenantFilteredPlans = normalizedPlans.filter(plan => {
                    const planTenantName = plan.tenant_name;
                    if (planTenantName && planTenantName === tenantName) return true;
                    if (!planTenantName && !plan.tenant_site_id) return true;
                    return false;
                });

                const nonRemovedPlans = tenantFilteredPlans.filter(plan => plan.status !== 2);
                const removedPlans = tenantFilteredPlans
                    .filter(plan => plan.status === 2)
                    .sort((a, b) => a.care_plan_id - b.care_plan_id);

                const mainPlans = nonRemovedPlans
                    .filter(plan => plan.status !== 3)
                    .sort((a, b) => a.care_plan_id - b.care_plan_id);

                const archivePlans = nonRemovedPlans
                    .filter(plan => plan.status === 3)
                    .sort((a, b) => a.care_plan_id - b.care_plan_id);

                setCarePlans(mainPlans);
                setArchivedCarePlans(archivePlans);
                setRemovedCarePlans(removedPlans);

                if (data.customer_name) setCustomerName(data.customer_name);
                else if (data.customerName) setCustomerName(data.customerName);
                else fetchCustomerDetails();

                setActiveQuotesLoaded(false);
                checkActiveQuotes(mainPlans);
            } else {
                setCarePlans([]);
                setArchivedCarePlans([]);
                setRemovedCarePlans([]);
            }
        } catch (error) {
            console.error('Error fetching service plans:', error);
            setCarePlans([]);
            setArchivedCarePlans([]);
        }
    };

    const getCarePlanId = (plan) =>
        plan.care_plan_id || plan.CarePlanID || plan.id || plan.Id || null;

    const checkActiveQuotes = async (plans) => {
        const results = await Promise.all(
            plans.map(async (plan) => {
                const actualCarePlanId = getCarePlanId(plan);
                if (!actualCarePlanId) return { carePlanId: null, data: null };
                try {
                    const response = await fetchWithAuth(`${API_BASE_URL}/careplan/${actualCarePlanId}/latest-quotation`);
                    if (response.ok) {
                        const data = await response.json();
                        return { carePlanId: actualCarePlanId, data: data?.quotation_id ? data : null };
                    }
                } catch (error) {
                    console.error(`Error checking active quote for service plan #${actualCarePlanId}:`, error);
                }
                return { carePlanId: actualCarePlanId, data: null };
            })
        );

        const quotesMap = {};
        let latestQuote = null;
        let latestCarePlanId = null;

        for (const { carePlanId, data } of results) {
            if (!carePlanId) continue;
            if (data) {
                quotesMap[carePlanId] = { hasQuote: true, quotationId: data.quotation_id, dateCreated: data.date_created || null };
                if (!latestQuote || data.quotation_id > latestQuote.quotationId) {
                    latestQuote = { quotationId: data.quotation_id, dateCreated: data.date_created };
                    latestCarePlanId = carePlanId;
                }
            } else {
                quotesMap[carePlanId] = { hasQuote: false, quotationId: null, dateCreated: null };
            }
        }

        setActiveQuotes(quotesMap);
        setLatestActiveQuoteCarePlanId(latestCarePlanId);
        setActiveQuotesLoaded(true);
    };

    const fetchCustomerDetails = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/care-recipients/${id}`);
            if (response.ok) {
                const data = await response.json();
                setCustomerName(`${data.Name} ${data.Surname}`);
            }
        } catch (error) {
            console.error('Error fetching customer details:', error);
        }
    };

    const handleArchiveClick = (plan) => {
        setCarePlanToArchive(plan);
        setShowArchiveModal(true);
    };

    const handleArchiveConfirm = async () => {
        if (!carePlanToArchive) return;
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/careplans/${id}/${carePlanToArchive.care_plan_id}`, {
                method: 'PUT',
                body: JSON.stringify({ ...carePlanToArchive, status: 3 })
            });
            if (response.ok) {
                const archived = { ...carePlanToArchive, status: 3 };
                setCarePlans(prev => prev.filter(p => p.care_plan_id !== carePlanToArchive.care_plan_id));
                setArchivedCarePlans(prev => [...prev, archived].sort((a, b) => a.care_plan_id - b.care_plan_id));
                showToast(`Service Plan ${formatCarePlanId(carePlanToArchive.care_plan_id)} archived successfully.`);
                setShowArchiveModal(false);
                setCarePlanToArchive(null);
            } else {
                showToast('Failed to archive service plan.', 'danger');
            }
        } catch (error) {
            showToast('An error occurred while archiving the service plan.', 'danger');
        }
    };

    const handleArchiveCancel = () => { setShowArchiveModal(false); setCarePlanToArchive(null); };

    const handleRemoveClick = (plan) => {
        setCarePlanToRemove(plan);
        setShowRemoveModal(true);
    };

    const handleRemoveConfirm = async () => {
        if (!carePlanToRemove) return;
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/careplans/${id}/${carePlanToRemove.care_plan_id}`, {
                method: 'PUT',
                body: JSON.stringify({ ...carePlanToRemove, status: 2 })
            });
            if (response.ok) {
                const removed = { ...carePlanToRemove, status: 2 };
                setCarePlans(prev => prev.filter(p => p.care_plan_id !== carePlanToRemove.care_plan_id));
                setArchivedCarePlans(prev => prev.filter(p => p.care_plan_id !== carePlanToRemove.care_plan_id));
                setRemovedCarePlans(prev => [...prev, removed].sort((a, b) => a.care_plan_id - b.care_plan_id));
                showToast(`Service Plan ${formatCarePlanId(carePlanToRemove.care_plan_id)} removed successfully.`);
                setShowRemoveModal(false);
                setCarePlanToRemove(null);
            } else {
                showToast('Failed to remove service plan.', 'danger');
            }
        } catch (error) {
            showToast('An error occurred while removing the service plan.', 'danger');
        }
    };

    const handleRemoveCancel = () => { setShowRemoveModal(false); setCarePlanToRemove(null); };

    const handleUnarchive = async (plan) => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/careplans/${id}/${plan.care_plan_id}`, {
                method: 'PUT',
                body: JSON.stringify({ ...plan, status: 0 })
            });
            if (response.ok) {
                const unarchived = { ...plan, status: 0 };
                setArchivedCarePlans(prev => prev.filter(p => p.care_plan_id !== plan.care_plan_id));
                setCarePlans(prev => [...prev, unarchived].sort((a, b) => a.care_plan_id - b.care_plan_id));
                showToast(`Service Plan ${formatCarePlanId(plan.care_plan_id)} unarchived successfully.`);
            } else {
                showToast('Failed to unarchive service plan.', 'danger');
            }
        } catch (error) {
            showToast('An error occurred while unarchiving the service plan.', 'danger');
        }
    };

    const handleArchiveAllClick = () => setShowArchiveAllModal(true);

    const handleArchiveAllConfirm = async () => {
        const plansToArchive = carePlans.filter(plan => plan.status !== 1);
        if (plansToArchive.length === 0) {
            showToast('No service plans to archive.', 'warning');
            setShowArchiveAllModal(false);
            return;
        }
        try {
            const archivePromises = plansToArchive.map(plan =>
                fetchWithAuth(`${API_BASE_URL}/careplans/${id}/${plan.care_plan_id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ ...plan, status: 3 })
                })
            );
            const results = await Promise.allSettled(archivePromises);
            const successfulIds = new Set(
                plansToArchive
                    .filter((_, i) => results[i].status === 'fulfilled' && results[i].value?.ok)
                    .map(p => p.care_plan_id)
            );
            const failedCount = plansToArchive.length - successfulIds.size;

            if (successfulIds.size > 0) {
                const archivedPlans = plansToArchive
                    .filter(p => successfulIds.has(p.care_plan_id))
                    .map(p => ({ ...p, status: 3 }));
                setCarePlans(prev => prev.filter(p => !successfulIds.has(p.care_plan_id)));
                setArchivedCarePlans(prev => [...prev, ...archivedPlans].sort((a, b) => a.care_plan_id - b.care_plan_id));
                if (failedCount > 0) {
                    showToast(`Archived ${successfulIds.size} service plan(s), but ${failedCount} failed.`, 'warning');
                } else {
                    showToast(`Successfully archived ${successfulIds.size} service plan(s).`);
                }
            } else {
                showToast('Failed to archive service plans.', 'danger');
            }
            setShowArchiveAllModal(false);
        } catch (error) {
            showToast('An error occurred while archiving service plans.', 'danger');
            setShowArchiveAllModal(false);
        }
    };

    const handleArchiveAllCancel = () => setShowArchiveAllModal(false);

    useEffect(() => {
        if (!currentTenant) return;
        fetchCarePlans();
    }, [id, currentTenant?.id, currentTenant?.name, sites]);

    if (!currentTenant) {
        return (
            <div className="container mt-4">
                <LoadingSpinner message="Loading tenant information..." />
            </div>
        );
    }

    const tabBtn = (key, label, count) => (
        <button
            type="button"
            onClick={() => setActiveTab(key)}
            style={{
                padding: '10px 22px',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === key ? `2px solid ${C.accent}` : '2px solid transparent',
                marginBottom: '-2px',
                fontWeight: activeTab === key ? 700 : 500,
                color: activeTab === key ? 'var(--theme-text)' : 'var(--theme-text-muted)',
                cursor: 'pointer',
                fontSize: '0.92rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition: 'color 0.15s',
            }}
        >
            {label}
            {count > 0 && (
                <span style={{
                    background: activeTab === key ? C.accent : '#9ca3af',
                    color: '#fff',
                    borderRadius: 10,
                    padding: '1px 7px',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    lineHeight: '1.5',
                }}>
                    {count}
                </span>
            )}
        </button>
    );

    const activePlanCount = carePlans.filter(p => p.status === 1).length;

    return (
        <div className="care-recipients-page">
            <div className="cr-content">
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="cr-page-hero">
                <div className="cr-page-hero-text">
                    <h1 className="cr-page-title">Service Plans</h1>
                    <p className="cr-page-subtitle">{customerName || 'View and manage service plans for this recipient'}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div className="cr-stat-chips">
                        <div className="cr-stat-chip">
                            <div className="cr-stat-value cr-stat-value--primary">{carePlans.length}</div>
                            <div className="cr-stat-label">Plans</div>
                        </div>
                        <div className="cr-stat-chip">
                            <div className="cr-stat-value cr-stat-value--green">{activePlanCount}</div>
                            <div className="cr-stat-label">Active</div>
                        </div>
                    </div>
                    {activeTab === 'active' && carePlans.some(p => p.status !== 1) && (
                        <PermissionGuard
                            permission={PERMISSIONS.SERVICE_PLAN_ARCHIVE}
                            showDisabled
                            tooltip="You do not have permission to archive service plans"
                        >
                            <Button
                                variant="warning"
                                size="sm"
                                onClick={handleArchiveAllClick}
                                style={{ fontWeight: 600 }}
                            >
                                Archive All Inactive
                            </Button>
                        </PermissionGuard>
                    )}
                </div>
            </div>

            {/* ── Content ─────────────────────────────────────────────────── */}
            <div>
                {/* Tab strip */}
                <div style={{ display: 'flex', borderBottom: `2px solid ${C.border}`, marginBottom: 20 }}>
                    {tabBtn('active', 'Service Plans', carePlans.length)}
                    {hasPermission(PERMISSIONS.SERVICE_PLAN_VIEW_ARCHIVED) && tabBtn('archived', 'Archived', archivedCarePlans.length)}
                    {hasPermission(PERMISSIONS.SERVICE_PLAN_VIEW_REMOVED) && tabBtn('removed', 'Removed', removedCarePlans.length)}
                </div>

                {/* Table card */}
                <div style={{ background: 'var(--theme-surface)', borderRadius: 12, border: '1px solid var(--theme-border)', boxShadow: '0 1px 2px var(--theme-shadow)', overflow: 'hidden' }}>
                    {activeTab === 'active' && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: C.headerBg }}>
                                    <th style={TH}>#</th>
                                    <th style={TH}>Date Created</th>
                                    <th style={TH}>Service Plan ID</th>
                                    <th style={TH}>Tenant</th>
                                    <th style={TH}>Site</th>
                                    <th style={TH}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {carePlans.length > 0 ? (
                                    carePlans.map((plan, index) => (
                                        <tr
                                            key={plan.care_plan_id}
                                            onClick={() => navigate(`/careplans/${id}/${plan.care_plan_id}`)}
                                            style={{ background: index % 2 === 0 ? C.surface : C.rowAlt, cursor: 'pointer', transition: 'background 0.12s' }}
                                            onMouseEnter={e => e.currentTarget.style.background = C.rowHover}
                                            onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? C.surface : C.rowAlt}
                                        >
                                            <td style={{ ...TD, color: C.textMuted, width: 48 }}>{index + 1}</td>
                                            <td style={TD}>{plan.date_created}</td>
                                            <td style={TD}>
                                                <Link
                                                    to={`/careplans/${id}/${plan.care_plan_id}`}
                                                    style={{ color: C.blue, fontWeight: 600, textDecoration: 'none' }}
                                                >
                                                    {formatCarePlanId(plan.care_plan_id)}
                                                </Link>
                                            </td>
                                            <td style={TD}>{plan.tenant_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                            <td style={TD}>{plan.site_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                            <td style={{ ...TD, width: 160 }} onClick={e => e.stopPropagation()}>
                                                {plan.status === 1 ? (
                                                    <span style={{
                                                        background: '#dcfce7',
                                                        color: '#166534',
                                                        borderRadius: 20,
                                                        padding: '3px 12px',
                                                        fontSize: '0.78rem',
                                                        fontWeight: 700,
                                                        letterSpacing: '0.3px',
                                                    }}>
                                                        Active
                                                    </span>
                                                ) : (
                                                    carePlans.length > 1 && (
                                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                            <PermissionGuard
                                                                permission={PERMISSIONS.SERVICE_PLAN_ARCHIVE}
                                                                showDisabled
                                                                tooltip="You do not have permission to archive service plans"
                                                            >
                                                                <Button
                                                                    variant="outline-warning"
                                                                    size="sm"
                                                                    onClick={() => handleArchiveClick(plan)}
                                                                    title="Archive"
                                                                    style={{ padding: '4px 8px', lineHeight: 1 }}
                                                                >
                                                                    <FaArchive size={12} />
                                                                </Button>
                                                            </PermissionGuard>
                                                            <PermissionGuard
                                                                permission={PERMISSIONS.SERVICE_PLAN_DELETE}
                                                                showDisabled
                                                                tooltip="You do not have permission to remove service plans"
                                                            >
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveClick(plan)}
                                                                    title="Remove permanently"
                                                                    style={{ padding: '4px 8px', lineHeight: 1 }}
                                                                >
                                                                    <FaTrash size={12} />
                                                                </Button>
                                                            </PermissionGuard>
                                                        </div>
                                                    )
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: C.textMuted, fontStyle: 'italic' }}>
                                            No service plans available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {activeTab === 'archived' && hasPermission(PERMISSIONS.SERVICE_PLAN_VIEW_ARCHIVED) && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: C.headerBg }}>
                                    <th style={TH}>#</th>
                                    <th style={TH}>Date Created</th>
                                    <th style={TH}>Service Plan ID</th>
                                    <th style={TH}>Tenant</th>
                                    <th style={TH}>Site</th>
                                    <th style={TH}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {archivedCarePlans.length > 0 ? (
                                    archivedCarePlans.map((plan, index) => (
                                        <tr
                                            key={plan.care_plan_id}
                                            onClick={() => navigate(`/careplans/${id}/${plan.care_plan_id}`)}
                                            style={{ background: index % 2 === 0 ? C.surface : C.rowAlt, cursor: 'pointer', transition: 'background 0.12s' }}
                                            onMouseEnter={e => e.currentTarget.style.background = C.rowHover}
                                            onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? C.surface : C.rowAlt}
                                        >
                                            <td style={{ ...TD, color: C.textMuted, width: 48 }}>{index + 1}</td>
                                            <td style={TD}>{plan.date_created}</td>
                                            <td style={TD}>
                                                <Link
                                                    to={`/careplans/${id}/${plan.care_plan_id}`}
                                                    style={{ color: C.blue, fontWeight: 600, textDecoration: 'none' }}
                                                >
                                                    {formatCarePlanId(plan.care_plan_id)}
                                                </Link>
                                            </td>
                                            <td style={TD}>{plan.tenant_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                            <td style={TD}>{plan.site_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                            <td style={{ ...TD, width: 160 }} onClick={e => e.stopPropagation()}>
                                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                    <PermissionGuard
                                                        permission={PERMISSIONS.SERVICE_PLAN_RESTORE}
                                                        showDisabled
                                                        tooltip="You do not have permission to unarchive service plans"
                                                    >
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            onClick={() => handleUnarchive(plan)}
                                                            style={{ fontWeight: 600, fontSize: '0.8rem', padding: '4px 12px' }}
                                                        >
                                                            Unarchive
                                                        </Button>
                                                    </PermissionGuard>
                                                    <PermissionGuard
                                                        permission={PERMISSIONS.SERVICE_PLAN_DELETE}
                                                        showDisabled
                                                        tooltip="You do not have permission to remove service plans"
                                                    >
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveClick(plan)}
                                                            title="Remove permanently"
                                                            style={{ padding: '4px 8px', lineHeight: 1 }}
                                                        >
                                                            <FaTrash size={12} />
                                                        </Button>
                                                    </PermissionGuard>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: C.textMuted, fontStyle: 'italic' }}>
                                            No archived service plans
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {activeTab === 'removed' && hasPermission(PERMISSIONS.SERVICE_PLAN_VIEW_REMOVED) && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: C.headerBg }}>
                                    <th style={TH}>#</th>
                                    <th style={TH}>Date Created</th>
                                    <th style={TH}>Service Plan ID</th>
                                    <th style={TH}>Tenant</th>
                                    <th style={TH}>Site</th>
                                </tr>
                            </thead>
                            <tbody>
                                {removedCarePlans.length > 0 ? (
                                    removedCarePlans.map((plan, index) => (
                                        <tr
                                            key={plan.care_plan_id}
                                            onClick={() => navigate(`/careplans/${id}/${plan.care_plan_id}`)}
                                            style={{ background: index % 2 === 0 ? C.surface : C.rowAlt, cursor: 'pointer', transition: 'background 0.12s' }}
                                            onMouseEnter={e => e.currentTarget.style.background = C.rowHover}
                                            onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? C.surface : C.rowAlt}
                                        >
                                            <td style={{ ...TD, color: C.textMuted, width: 48 }}>{index + 1}</td>
                                            <td style={TD}>{plan.date_created}</td>
                                            <td style={TD}>
                                                <Link
                                                    to={`/careplans/${id}/${plan.care_plan_id}`}
                                                    style={{ color: '#dc2626', fontWeight: 600, textDecoration: 'none' }}
                                                >
                                                    {formatCarePlanId(plan.care_plan_id)}
                                                </Link>
                                            </td>
                                            <td style={TD}>{plan.tenant_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                            <td style={TD}>{plan.site_name || <span style={{ color: C.textMuted }}>—</span>}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: C.textMuted, fontStyle: 'italic' }}>
                                            No removed service plans
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            </div>

            {/* ── Archive modal ────────────────────────────────────────────── */}
            <Modal show={showArchiveModal} onHide={handleArchiveCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Archive Service Plan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to archive Service Plan {formatCarePlanId(carePlanToArchive?.care_plan_id)}?
                    Archived plans are hidden from the main list but remain accessible via the "Archived" tab and can be unarchived at any time.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleArchiveCancel}>Cancel</Button>
                    <Button variant="warning" onClick={handleArchiveConfirm}>Archive</Button>
                </Modal.Footer>
            </Modal>

            {/* ── Remove modal ─────────────────────────────────────────────── */}
            <Modal show={showRemoveModal} onHide={handleRemoveCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Remove Service Plan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to remove Service Plan {formatCarePlanId(carePlanToRemove?.care_plan_id)}?</p>
                    <p className="text-danger mb-0">
                        <strong>Warning:</strong> Removing this service plan will effectively delete it from the platform.
                        It will no longer be accessible from any list and this action cannot be undone.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleRemoveCancel}>Cancel</Button>
                    <Button variant="danger" onClick={handleRemoveConfirm}>Remove</Button>
                </Modal.Footer>
            </Modal>

            {/* ── Archive All modal ────────────────────────────────────────── */}
            <Modal show={showArchiveAllModal} onHide={handleArchiveAllCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Archive All Inactive Service Plans</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {(() => {
                        const activePlan = carePlans.find(p => p.status === 1);
                        const toArchive = carePlans.filter(p => p.status !== 1);
                        return (
                            <>
                                {activePlan && (
                                    <p>Service plan <strong>{formatCarePlanId(activePlan.care_plan_id)}</strong> is active and will be kept.</p>
                                )}
                                <p>
                                    <strong>{toArchive.length}</strong> service plan(s) will be archived and accessible via the "Archived" tab.
                                </p>
                            </>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleArchiveAllCancel}>Cancel</Button>
                    <Button variant="warning" onClick={handleArchiveAllConfirm}>Archive All</Button>
                </Modal.Footer>
            </Modal>

            {toast.show && (
                <div className={`app-toast app-toast-${toast.variant}`}>
                    {toast.message}
                </div>
            )}
        </div>
    );
}

export default CarePlans;
